import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { xpApi, participantAuth, formatCents } from "@/lib/experienceApi";
import { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Copy, Ticket, ArrowRight, FileDown } from "lucide-react";
import { toast } from "sonner";

export default function ExperienceCheckoutSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const orderId = params.get("order_id");

  useEffect(() => {
    if (!orderId) return;
    // Poll up to 10s for webhook completion.
    let tries = 0;
    const tick = async () => {
      try {
        const res = await xpApi.orderStatus(orderId);
        setData(res);
        if (res.order?.status !== "paid" && tries < 10) {
          tries += 1;
          setTimeout(tick, 1000);
        }
      } catch {}
    };
    tick();
  }, [orderId]);

  const enterAs = async (code) => {
    try {
      const res = await xpApi.participantLogin(code);
      participantAuth.setToken(res.session_token);
      participantAuth.setCtx({ ticket: res.ticket, event_id: res.event_id });
      nav("/experience/my");
    } catch (e) { toast.error("Impossible de se connecter"); }
  };

  if (!data) return <div className="p-6 text-center text-sm text-neutral-500">Vérification du paiement…</div>;
  const { order, tickets } = data;
  const paid = order.status === "paid";

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-display text-neutral-900">{paid ? "Paiement confirmé" : "Paiement en cours…"}</h1>
        <p className="text-sm text-neutral-600 mt-2">Un email de confirmation vous a été envoyé à <b>{order.customer_email}</b>.</p>
      </div>

      <Card className="mb-4" data-testid="order-breakdown">
        <CardContent className="p-4 text-sm">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Détail transparent</div>
          <div className="grid grid-cols-2 gap-1">
            <span>Montant brut</span><span className="text-right">{formatCents(order.amount_gross_cents, order.currency)}</span>
            <span className="text-neutral-500">Frais Stripe (estim.)</span><span className="text-right text-neutral-500">-{formatCents(order.stripe_fee_cents, order.currency)}</span>
            <span className="text-neutral-500">Commission geoVINUM</span><span className="text-right text-neutral-500">-{formatCents(order.application_fee_cents, order.currency)}</span>
            <span className="font-semibold">Net organisateur</span><span className="text-right font-semibold">{formatCents(order.amount_net_cents, order.currency)}</span>
          </div>
        </CardContent>
      </Card>

      <h2 className="font-semibold mb-2">Vos codes participant</h2>
      <div className="grid gap-3 mb-6">
        {tickets.map(t => (
          <Card key={t.id} data-testid={`ticket-${t.id}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-2xl font-mono font-bold text-[#E5276A]">
                  <Ticket className="w-5 h-5" /> {t.participant_code}
                </div>
                <div className="text-xs text-neutral-500 mt-1">{t.tastings_remaining} dégustations disponibles</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(t.participant_code); toast.success("Copié"); }}>
                  <Copy className="w-4 h-4" />
                </Button>
                <a href={`${API}/experience/tickets/${t.id}/pdf`} target="_blank" rel="noreferrer"
                   data-testid={`pdf-${t.id}`}>
                  <Button variant="outline" size="sm">
                    <FileDown className="w-4 h-4" />
                  </Button>
                </a>
                <Button size="sm" onClick={() => enterAs(t.participant_code)} className="bg-[#E5276A] hover:bg-[#C01661]">
                  Entrer <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
